(() => {
  if (customElements.get('shop-the-look-v2')) return;

  class ShopTheLookV2 extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.mobileLayout = this.dataset.mobileLayout;
      this.gapMobile = parseInt(this.dataset.gapMobile, 10) || 8;
      this.mediaQuery = window.matchMedia('(max-width: 989px)');
      this._onKeyDown = this._handleKeyDown.bind(this);
    }

    connectedCallback() {
      this.initSlider();
      this.mediaQuery.addEventListener('change', this.onBreakpointChange.bind(this));

      this.querySelectorAll('.stl-v2__item').forEach((item) => {
        item.addEventListener('click', this.openDrawer.bind(this));
      });

      this.querySelectorAll('.stl-v2-drawer__overlay, .stl-v2-drawer__close').forEach((el) => {
        el.addEventListener('click', this.closeDrawerFromEl.bind(this));
      });

      document.addEventListener('keydown', this._onKeyDown);

      // Capture-phase submit intercept — runs before product-form's bubble handler
      this.addEventListener('submit', this.handleAddToCart.bind(this), { capture: true });

      // Own variant selection — no dependency on variant-radios internals
      this._initVariantSelection();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeyDown);
      this.destroySlider();
    }

    // ── Variant selection ─────────────────────────────────────
    // We manage size selection ourselves instead of relying on variant-radios,
    // which uses document.getElementById by section ID and is incompatible with
    // our per-product context IDs inside the drawer.

    _initVariantSelection() {
      this.querySelectorAll('.stl-v2-drawer .stl-v2-card').forEach((card) => {
        const variantDataEl = card.querySelector('.stl-v2-card__variant-data');
        if (!variantDataEl) return;

        const variants = JSON.parse(variantDataEl.textContent);
        if (!variants.length) return;

        const form = card.querySelector('form[id^="product-form-"]');
        if (!form) return;

        const hiddenInput = card.querySelector('.product-variant-id');
        const submitBtn = form.querySelector('button[name="add"]');
        const numOptions = variants[0].options.length;

        // Clear the pre-rendered default so nothing is added until a size is chosen
        if (hiddenInput) hiddenInput.value = '';

        card.querySelectorAll('.stl-v2-card__option input[type="radio"]').forEach((radio) => {
          radio.addEventListener('change', () => {
            // Build a selectedOptions array indexed by actual variant option position.
            // Colour options are hidden in the UI so their index stays null (matches any).
            const selectedOptions = new Array(numOptions).fill(null);
            card.querySelectorAll('.stl-v2-card__option').forEach((optionGroup) => {
              const idx = parseInt(optionGroup.dataset.optionIndex, 10);
              const checked = optionGroup.querySelector('input[type="radio"]:checked');
              if (!isNaN(idx)) selectedOptions[idx] = checked ? checked.value : null;
            });

            // Find a variant whose options match every selected slot (null = wildcard)
            const matched = variants.find((v) =>
              v.options.every((opt, i) =>
                selectedOptions[i] === null ? true : selectedOptions[i] === opt
              )
            );

            if (!matched || !hiddenInput) return;

            hiddenInput.value = matched.id;

            if (submitBtn && !submitBtn.classList.contains('checkmark')) {
              const span = submitBtn.querySelector('span');
              if (matched.available) {
                submitBtn.removeAttribute('disabled');
                if (span) span.textContent = 'ADD TO BAG';
              } else {
                submitBtn.setAttribute('disabled', 'disabled');
                if (span) span.textContent = 'SOLD OUT';
              }
            }
          });
        });
      });
    }

    // ── Slider ────────────────────────────────────────────────

    initSlider() {
      if (this.mobileLayout !== 'slider') return;
      if (!this.mediaQuery.matches) return;
      if (this.slider) return;

      const sliderEl = this.querySelector('[data-slider]');
      if (!sliderEl) return;

      this.slider = new Swiper(sliderEl, {
        slidesPerView: 2,
        spaceBetween: this.gapMobile,
        loop: false,
      });
    }

    destroySlider() {
      if (this.slider) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
    }

    onBreakpointChange(e) {
      if (e.matches) {
        this.initSlider();
      } else {
        this.destroySlider();
      }
    }

    // ── Drawer ────────────────────────────────────────────────

    openDrawer(e) {
      const item = e.currentTarget;
      const drawerId = item.dataset.drawer;
      const drawer = document.getElementById(drawerId);
      if (!drawer) return;

      this.closeAllDrawers();
      drawer.removeAttribute('hidden');
      document.body.classList.add('overflow-hidden');

      // Double rAF ensures the browser paints the display change before the
      // CSS transition fires (avoids instant-snap when removing [hidden]).
      requestAnimationFrame(() => requestAnimationFrame(() => {
        drawer.classList.add('is-open');
      }));

      const panel = drawer.querySelector('.stl-v2-drawer__panel');
      if (panel) panel.focus();
    }

    closeDrawerFromEl(e) {
      e.stopPropagation();
      const drawer = e.currentTarget.closest('.stl-v2-drawer');
      if (drawer) this._closeDrawer(drawer);
    }

    closeAllDrawers() {
      this.querySelectorAll('.stl-v2-drawer.is-open').forEach((d) => this._closeDrawer(d));
    }

    _closeDrawer(drawer) {
      drawer.classList.remove('is-open');
      const panel = drawer.querySelector('.stl-v2-drawer__panel');
      const onEnd = () => {
        drawer.setAttribute('hidden', '');
        document.body.classList.remove('overflow-hidden');
      };
      if (panel) {
        panel.addEventListener('transitionend', onEnd, { once: true });
      } else {
        onEnd();
      }
    }

    _handleKeyDown(e) {
      if (e.key === 'Escape') this.closeAllDrawers();
    }

    // ── Add to cart ───────────────────────────────────────────

    async handleAddToCart(evt) {
      const form = evt.target;

      if (!form.closest('.stl-v2-drawer')) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();

      const productForm = form.closest('product-form');
      const submitBtn = productForm ? productForm.querySelector('[type="submit"]') : null;
      if (!submitBtn || submitBtn.getAttribute('aria-disabled') === 'true') return;

      const variantInput = form.querySelector('.product-variant-id');
      const variantId = variantInput ? variantInput.value : null;

      if (!variantId) {
        this._showError(productForm, 'Please select a size');
        return;
      }

      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('loading');
      this._clearError(productForm);

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });

        if (res.ok) {
          submitBtn.classList.remove('loading');
          submitBtn.removeAttribute('aria-disabled');
          this._updateCartCount();
          // this.closeAllDrawers();
          window.theme?.openCart?.();
        } else {
          const data = await res.json().catch(() => ({}));
          submitBtn.classList.remove('loading');
          submitBtn.removeAttribute('aria-disabled');
          this._showError(productForm, data.description || 'Could not add to bag. Please try again.');
        }
      } catch {
        submitBtn.classList.remove('loading');
        submitBtn.removeAttribute('aria-disabled');
      }
    }

    _showError(productForm, message) {
      if (!productForm) return;
      const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
      const msg = productForm.querySelector('.product-form__error-message');
      if (wrapper && msg) {
        msg.textContent = message;
        wrapper.removeAttribute('hidden');
      }
    }

    _clearError(productForm) {
      if (!productForm) return;
      const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
      if (wrapper) wrapper.setAttribute('hidden', '');
    }

    async _updateCartCount() {
      try {
        const cart = await fetch('/cart.js').then((r) => r.json());
        const count = cart.item_count;

        document.querySelectorAll('.cart-count-bubble').forEach((el) => {
          el.innerHTML = `<span aria-hidden="true">${count}</span><span class="visually-hidden">${count} item${count !== 1 ? 's' : ''}</span>`;
          el.classList.remove('hidden');
        });

        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = count;
        });
      } catch {
        // fail silently
      }
    }
  }

  customElements.define('shop-the-look-v2', ShopTheLookV2);
})();
